import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICondoBookingSummary,
  ICondoGuestBooking,
  IErrorField,
  ICondoBookPackageWithCard,
  ICondoBookingComplete,
  ICondoCardBooking,
  IInsuranceRequestTravelers,
} from '@common-types';
import { ISessionKey, BookingErrorsEnum, ItemTypeEnum } from '@share/common-types';
import { get, isString } from 'lodash';
import {
  attachCertificateContextToBody,
  buildCertificateContextFromSelection,
  getHeaders,
  axiosInstance,
  AppThunk,
  UrlUtils,
  getSelectedCurrency,
  ICertificateRouteHint,
  RootState,
  isCertificateApplied,
  isDisplayCertificates,
  getSessionObj,
} from '@share/utils';
import ReactGA from 'react-ga4';
import {
  ERROR,
  EXTERNAL_ERROR,
  PRICE_CHANGED,
  STATUS_CONFIRMED,
  STATUS_SUCCEEDED,
  VALIDATION_ERROR,
  STATUS_FAILED,
  STATUS_PENDING,
  CONDO_BOOKING_GUEST_INFO_LABEL,
  CONDO_BOOKING_CARD_INFO_LABEL,
  CONDO_BOOKING_SAME_ADDRESS_INFO_LABEL,
  SAVING_MISMATCH,
  CHECK_OUT_PAYMENT_ERROR,
  POSTAL_CODE_PAYMENT_ERROR,
  STATE_PAYMENT_ERROR,
  DECLINE_PAYMENT_ERROR,
  USD_CURRENCY,
  USA_STATES,
} from '@constants';
import {
  SESSION_EXPIRED_STATUS,
  SESSION_SEARCH_EXPIRED_MESSAGE,
  Urls,
  CONDO_UNITS_SESSION_KEY_LABEL,
  CONDO_SESSION_KEY,
  CONDO_UNITS_SEARCH_LABEL,
  C_C_BOOKING_CONFIRMED, 
} from '@share/constants';
import { getAccountCreditLine, getUserWallet, condosActions, getUserWalletReward, clearUserCertificateSearchContext, prefetchCrossSell } from '@share/store/slices';
import { setThreeDSId, setThreeDSLoading, setThreeDSModalVisible, setThreeDSUrl } from './review-book';
import { getInsuranceQuote, insuranceActions, InsuranceSelection } from './insurance';
import moment from 'moment';
import { getCondoAddressFromStorage } from '@utils';
import { StripeChargeStatus } from '../../share/utils/stripe';
import { confirmPaymentWithEmbeddedNextActions, isStripeExpressAuthorized, isStripeExpressPending, type StripeDirectConfirm } from '../../share/utils/stripe-confirm-payment';
import { buildStripeExpressReturnUrl } from '../../share/utils/stripe-return-url';
import { Stripe, StripeElement } from '@stripe/stripe-js';

export interface ICondoReviewBookState {
  condoBookingSummary: ICondoBookingSummary | null;
  loading: boolean;
  error: string;
  isUpdatePrice: boolean;
  guest: ICondoGuestBooking | null | undefined;
  card: ICondoCardBooking | null | undefined;
  errorsField: IErrorField[] | null | undefined;
  loadingBooking: boolean;
  bookingComplete: boolean;
  isBookingInProgress: boolean;
  booking: ICondoBookingComplete | null | undefined;
  errorBooking: string;
  isExternalError: boolean;
  isPriceChangedError: boolean;
  bookingErrorCode: BookingErrorsEnum | null;
  errorsBookingMessage: IErrorField[] | null | undefined;
  errorCountries: string;
  expiredSession: boolean;
  isBookingPending: boolean;
  isAlternativeUnitsAvailable: boolean;
  isErrorMessageExpirationDate: boolean;
  isErrorMessageCardNumber: boolean;
  isErrorMessageZipCode: boolean;
  isSavingsMismatch: boolean;
  selectedCondoReviewClientCash?: number | null | undefined;
  selectedCondoReviewClientCashStr?: string;
  errorStripe: string;
  stripeLoading: boolean;
  rewardId: number | null;
}

const initialState: ICondoReviewBookState = {
  condoBookingSummary: null,
  loading: false,
  error: '',
  isUpdatePrice: false,
  guest: null,
  errorsField: null,
  card: null,
  loadingBooking: false,
  bookingComplete: false,
  isBookingInProgress: false,
  booking: null,
  errorBooking: '',
  isExternalError: false,
  isPriceChangedError: false,
  errorsBookingMessage: null,
  errorCountries: '',
  expiredSession: false,
  bookingErrorCode: null,
  isBookingPending: false,
  isAlternativeUnitsAvailable: false,
  isErrorMessageExpirationDate: true,
  isErrorMessageCardNumber: true,
  isErrorMessageZipCode: true,
  isSavingsMismatch: false,
  selectedCondoReviewClientCash: null,
  selectedCondoReviewClientCashStr: '',
  errorStripe: '',
  stripeLoading: false,
  rewardId: null,
};

const zeroItem = 0;

const condoReviewBookSlice = createSlice({
  name: 'condo-review-book',
  initialState,
  reducers: {
    setCondoReviewBook: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<ICondoBookingSummary>,
    ) => {
      state.condoBookingSummary = payload;
    },
    setLoadingCondoBook: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setIsBookingPending: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isBookingPending = payload;
    },
    setIsAlternativeUnitsAvailable: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isAlternativeUnitsAvailable = payload;
    },
    setErrorCondoBook: (state: ICondoReviewBookState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setUpdateCondoPriceReview: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isUpdatePrice = payload;
    },
    setCondoGuest: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<ICondoGuestBooking | null | undefined>,
    ) => {
      state.guest = payload;
    },
    setCondoCard: (state: ICondoReviewBookState, { payload }: PayloadAction<ICondoCardBooking | null | undefined>) => {
      state.card = payload;
    },
    setLoadingCondoBooking: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.loadingBooking = payload;
    },
    setCondoBookingComplete: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.bookingComplete = payload;
    },
    setCondoBookingInProgress: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isBookingInProgress = payload;
    },
    setCondoBooking: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<ICondoBookingComplete | null | undefined>,
    ) => {
      state.booking = payload;
    },
    setCondoErrorBooking: (state: ICondoReviewBookState, { payload }: PayloadAction<string>) => {
      state.errorBooking = payload;
    },
    setErrorsCondoField: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<IErrorField[] | null | undefined>,
    ) => {
      state.errorsField = payload;
    },
    setStripeError: (state: ICondoReviewBookState, { payload }: PayloadAction<string>) => {
      state.errorStripe = payload;
    },
    setStripeLoading: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.stripeLoading = payload;
    },
    setCondoExternalError: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isExternalError = payload;
    },
    setCondoSavingsMismatch: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isSavingsMismatch = payload;
    },
    setCondoPriceChangedError: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isPriceChangedError = payload;
    },
    setBookingErrorCodeError: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<BookingErrorsEnum | null>,
    ) => {
      state.bookingErrorCode = payload;
    },
    setCondoErrorsBookingMessage: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<IErrorField[] | null | undefined>,
    ) => {
      state.errorsBookingMessage = payload;
    },
    setErrorCountries: (state: ICondoReviewBookState, { payload }: PayloadAction<string>) => {
      state.errorCountries = payload;
    },
    setCondoExpiredSession: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.expiredSession = payload;
    },
    setSelectedCondoReviewClientCash: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<number | null | undefined>,
    ) => {
      state.selectedCondoReviewClientCash = payload;
    },
    setSelectedCondoReviewClientCashStr: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<string | undefined>,
    ) => {
      state.selectedCondoReviewClientCashStr = payload;
    },
    setCondoReviewRewardId: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<number | null>,
    ) => {
      state.rewardId = payload;
    },
    condoBookingResetState: () => {
      return initialState;
    },
    setErrorMessageExpirationDate: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isErrorMessageExpirationDate = payload;
    },
    setErrorMessageCardNumber: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isErrorMessageCardNumber = payload;
    },
    setErrorMessageZipCode: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isErrorMessageZipCode = payload;
    },
  },
});

export const {
  setCondoReviewBook,
  setLoadingCondoBook,
  setErrorCondoBook,
  setUpdateCondoPriceReview,
  setCondoGuest,
  setCondoCard,
  setLoadingCondoBooking,
  setCondoBookingComplete,
  setCondoBookingInProgress,
  setCondoBooking,
  setCondoErrorBooking,
  setErrorsCondoField,
  setCondoExternalError,
  setCondoPriceChangedError,
  setCondoErrorsBookingMessage,
  setErrorCountries,
  setCondoExpiredSession,
  setBookingErrorCodeError,
  setIsBookingPending,
  setIsAlternativeUnitsAvailable,
  condoBookingResetState,
  setErrorMessageExpirationDate,
  setErrorMessageCardNumber,
  setErrorMessageZipCode,
  setCondoSavingsMismatch,
  setSelectedCondoReviewClientCash,
  setSelectedCondoReviewClientCashStr,
  setCondoReviewRewardId,
  setStripeError,
  setStripeLoading,
} = condoReviewBookSlice.actions;

export const condoReviewBookReducer = condoReviewBookSlice.reducer;

export const getCondoReviewBook = (
  condoId: number,
  availabilityId: string,
  sessionKey: ISessionKey,
  isGetCoupon?: false,
  quote?: string,
  certificateRoute?: ICertificateRouteHint,
): AppThunk => {
  return async (dispatch, getState) => {
    const { condoRedeemCodeStore, condoReviewBookStore, loginStore, marginatorStore, condoFiltersStore, certificatesStore } =
      getState() as RootState;
    const { account } = loginStore;
    const { card } = condoReviewBookStore;

    if (!(condoRedeemCodeStore.isGetCoupon || isGetCoupon)) {
      dispatch(setLoadingCondoBook(true));
    }

    dispatch(setUpdateCondoPriceReview(true));
    dispatch(setCondoExpiredSession(false));

    const currency = getSelectedCurrency(account);
   
    try {
      const certificateApplied = isCertificateApplied(account, condoFiltersStore.certificateId);
      const percentage = marginatorStore.percentage || 0;

      const requestBody: Record<string, unknown> = {
        condoId,
        availabilityId,
        sessionKey,
        quote,
        currency,
      };

      if (!certificateApplied) {
        requestBody.marginator = { percentage, commission: marginatorStore.commission };
      }

      const { loginStore, condoReviewBookStore, condosStore, navigationMenuStore } = getState();
      const { userWalletData } = loginStore;
      const { selectedCondoReviewClientCash } = condoReviewBookStore;
      const { selectedCondoSearchClientCash } = condosStore;
      const { items } = navigationMenuStore;

      const displayCertificates = isDisplayCertificates(account);
      if (displayCertificates) {
        const certificateContext = buildCertificateContextFromSelection({
          certificates: certificatesStore?.data,
          itemType: ItemTypeEnum.Condo,
          storeCertificateId: condoFiltersStore.certificateId,
          storeCertificateItemId: condoFiltersStore.certificateItemId,
          routeCertificateId: certificateRoute?.certificateId,
          routeCertificateItemId: certificateRoute?.certificateItemId,
        });
        attachCertificateContextToBody(requestBody, certificateContext);
      }

      const res = await axiosInstance.post(
        Urls.CondoBookingSummary,
        requestBody,
        { ...getHeaders() },
      );

      if (!certificateApplied && !!selectedCondoReviewClientCash) {
        const walletNoDecimals = account?.walletNoDecimals;
        const maxWalletClientCashAmount = items?.isMLM ? res.data?.bookingCard?.bookingPrice?.savings : res.data?.bookingCard?.bookingPrice?.maxWalletClientCash;
        const convertionRate = userWalletData?.convertionRate ? userWalletData?.convertionRate : 1;
        const maxWalletClientCashCalculated = maxWalletClientCashAmount / convertionRate;
        const maxWalletClientCash = walletNoDecimals ? Math.floor(maxWalletClientCashCalculated) : maxWalletClientCashCalculated;
        
        if (maxWalletClientCash < selectedCondoReviewClientCash) {
          dispatch(setSelectedCondoReviewClientCash(maxWalletClientCash));
          dispatch(setSelectedCondoReviewClientCashStr(maxWalletClientCash ? maxWalletClientCash.toString() : ''));
          dispatch(condosActions.setSelectedCondoSearchClientCash({ ...selectedCondoSearchClientCash, selectedPropertyReviewClientCash: maxWalletClientCash }));
        }
      }

      dispatch(setCondoReviewBook({ ...res.data }));

      dispatch(setUpdateCondoPriceReview(false));
      dispatch(setLoadingCondoBook(false));

      // Warm the cross-sell recommendations at recheck time, so they run in the
      // background while the user reviews & books and are cached by the time the
      // confirmation screen renders them. The backend decides which verticals are
      // active, so there's no front-end gating.
      dispatch(prefetchCrossSell({
        origin: 'condo',
        propertyId: condoId,
        checkIn: res.data?.bookingCard?.checkIn,
        checkOut: res.data?.bookingCard?.checkOut,
      }));

      // if certificateContext was applied, use balanceForInsurance, otherwise use balance
      const balanceForInsurance = requestBody?.certificateContext ? res.data?.bookingCard?.balanceForInsurance : res.data?.bookingCard?.balance;
      const balance = balanceForInsurance ?? res.data?.bookingCard?.balance;
      const checkIn = res.data?.bookingCard?.checkIn ? moment(res.data?.bookingCard?.checkIn, 'yyyy-MM-DD').format('yyyy-MM-DD') : null;
      const checkOut = res.data?.bookingCard?.checkOut ? moment(res.data?.bookingCard?.checkOut, 'yyyy-MM-DD').format('yyyy-MM-DD') : null;
      const travelers = res.data?.bookingCard?.totalGuests ? res.data?.bookingCard?.totalGuests : 1;
      const destinationCountry = res.data?.bookingCard?.countryCode ? res.data?.bookingCard?.countryCode : null;

      const addressFromStorage: any = getCondoAddressFromStorage();
      const country = addressFromStorage ? addressFromStorage.country ? addressFromStorage.country : card?.country : card?.country;
      const state = addressFromStorage ? addressFromStorage.state ? addressFromStorage.state : card?.state : card?.state;

      dispatch(insuranceActions.setBaseRequest({
        sessionId: sessionKey?.value,
        totalPrice: {
          isoCurrencyCode: USD_CURRENCY,
          value: balance
        },
        booking: {
          startDateTime: checkIn ?? '',
          endDateTime: checkOut ?? '',
          destinationIsoCountryCode: 'US'
        }  
      }));
      dispatch(insuranceActions.setTravelersRequest({ numberOfTravelers: 1 } as IInsuranceRequestTravelers));
  
      if (country === 'US' && !items?.isRemoveInsurance) {
        const isUSState = USA_STATES.map(s => s.postalCode).includes(state);

        dispatch(getInsuranceQuote(balance, USD_CURRENCY, checkIn ?? '', checkOut ?? '', isUSState ? state : null, travelers, destinationCountry, sessionKey?.value));
      }


    } catch (error: any) {
      const errorObject = JSON.parse(error?.response?.data);
      const errorCode = get(errorObject, 'code', null);
      const errorReason = get(errorObject, 'reason', error.toString());

      dispatch(setErrorCondoBook(errorReason));
      dispatch(setLoadingCondoBook(false));
      dispatch(setUpdateCondoPriceReview(false));

      dispatch(setCondoExternalError(errorCode === 'error'));

      if (BookingErrorsEnum.SoldOut === errorCode) {
        dispatch(setBookingErrorCodeError(BookingErrorsEnum.SoldOut));
      }
      if (BookingErrorsEnum.RoomsUnavailable === errorCode) {
        dispatch(setBookingErrorCodeError(BookingErrorsEnum.RoomsUnavailable));
      }

      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data[zeroItem] === SESSION_SEARCH_EXPIRED_MESSAGE
      ) {
        dispatch(setCondoExpiredSession(true));
      }
    }
  };
};

export const getBookingStripe =
  (
    bookingGuid: string,
    sessionKey: ISessionKey,
    paymentMethod: string | undefined,
    isCancel: boolean = false
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setStripeLoading(true));
    dispatch(setLoadingCondoBooking(true));

    // Build query params
    const params = {
      bookingId: bookingGuid,
      isCancel,
      sessionId: sessionKey.value,
      paymentDescripcion: paymentMethod,
    };

    try {
      // Single GET call with params
      const { data } = await axiosInstance.get(Urls.CondosResumeExpress, {
        ...getHeaders(),
        params,
      });

      dispatch(handleProcessBookingComplete(data));

    } catch (err: any) {
      // Unified error message extraction
      const errorObject = isString(err) ? JSON.parse(err) : err;
      const data = errorObject.response?.data;
      const dataObject = data && isString(data) ? JSON.parse(data) : data;
      const message = dataObject?.reason ?? errorObject.message ??  "Unknown error";

      dispatch(setStripeError(message));
      dispatch(handleErrorBookingComplete(err, null));
    } finally {
      // Tear down loading indicator
      dispatch(setStripeLoading(false));
      dispatch(setLoadingCondoBooking(false));
    }
  };


export const confirmPayment = (
  clientSecret: string,
  stripe: any,
  elements: any,
  bookingGui: string,
  paymentMethod: string | undefined,
  /** Set for Afterpay/Clearpay and Cash App Pay, which are confirmed through their own Stripe API
   *  rather than through Elements (no ExpressCheckoutElement button exists for them). Carries the
   *  `payment_method_types` token and the billing details Stripe needs to build the PaymentMethod.
   *  Undefined for card and the wallets. */
  directConfirm?: StripeDirectConfirm,
): AppThunk => {
  return async (dispatch, getState) => {
    const values = UrlUtils.getValues();
    const sessionKey = getSessionObj(
      values[CONDO_UNITS_SESSION_KEY_LABEL] as ISessionKey,
      values[CONDO_SESSION_KEY] as ISessionKey,
    );
    const accountUsername = getState().loginStore?.account?.name;
    const returnUrl = buildStripeExpressReturnUrl({
      accountUsername,
      vertical: 'condo',
      bookingGuid: bookingGui,
      sessionId: sessionKey.value,
      paymentMethod: paymentMethod as string,
      fallbackAbsoluteUrl: `${import.meta.env.VITE_API_BFF_URL}${Urls.CondosResumeExpress}?bookingId=${bookingGui}`,
    });

    try {
      const { paymentIntent, error } = await confirmPaymentWithEmbeddedNextActions(stripe, {
        elements,
        clientSecret,
        returnUrl,
        paymentMethodType: directConfirm?.paymentMethodType,
        billingDetails: directConfirm?.billingDetails,
      });

      // Authorized (normally `requires_capture` under manual capture): proceed to resume, which books
      // with the provider and then captures (or voids on failure). See `isStripeExpressAuthorized`
      // for the statuses that count — redirect / QR methods (Klarna, Afterpay, Cash App Pay) can also
      // land on `processing`, and treating that as a failure would void a good authorization.
      if (isStripeExpressAuthorized(paymentIntent?.status)) {
        dispatch(getBookingStripe(bookingGui, sessionKey, paymentMethod));
        dispatch(setStripeError(''));
      } else if (isStripeExpressPending(paymentIntent?.status)) {
        // Customer dismissed the action without finishing it — closing Cash App Pay's QR modal is the
        // case this exists for. The authorization is still live, so do NOT resume: cancelling here
        // would void a payment the customer may be approving on their phone, and the BFF rejects it
        // with a 400 anyway (TRAV23-4969). Just clear the spinners and let them choose again.
        dispatch(setStripeError(''));
        dispatch(setLoadingCondoBooking(false));
        dispatch(setStripeLoading(false));
      } else {
        // Anything else means the payment did not authorize. A hard
        // decline surfaces an `error`; a buyer cancellation (e.g. Amazon Pay BuyerCanceled)
        // resolves with no `error` and the PI in `requires_payment_method`. Both must resume
        // with isCancel=true so the pending express booking is voided and the UI recovers.
        dispatch(getBookingStripe(bookingGui, sessionKey, paymentMethod, true));
        dispatch(
          setStripeError(
            error?.message ??
              (paymentIntent as any)?.last_payment_error?.message ??
              'Payment was not completed. Please try again.',
          ),
        );
      }
    } catch (error: any) {
      if(typeof error === 'string') {
        dispatch(setStripeError(error));
      }
      else {
        const err = error.message ? error.message : error?.response.data;
        dispatch(setStripeError(err));
      }
      dispatch(getBookingStripe(bookingGui, sessionKey, paymentMethod, true));
      dispatch(handleErrorBookingComplete(error, null));
    }
  };
};

export const getCondoBookPackageWithCard = (
  bookWithCard: ICondoBookPackageWithCard,
  onErrorCallback: (errorCode: string) => void,
  stripe?: Stripe,
  elements?: StripeElement,
  paymentMethod?: string,
  /** Forwarded to {@link confirmPayment}: set only for Afterpay/Clearpay and Cash App Pay. */
  directConfirm?: StripeDirectConfirm,
): AppThunk => {
  return async (dispatch, getState) => {

    dispatch(setLoadingCondoBooking(true));
    dispatch(setBookingErrorCodeError(null));
    dispatch(setIsBookingPending(false));
    dispatch(setIsAlternativeUnitsAvailable(false));
    dispatch(setCondoExpiredSession(false));
    dispatch(setThreeDSModalVisible(false));
    dispatch(setThreeDSUrl(null));
    dispatch(setThreeDSId(null));
    dispatch(setThreeDSLoading(false));

    const { insuranceStore, loginStore, condoReviewBookStore } = getState();
    const { selection, insurance } = insuranceStore;
    const { account } = loginStore;
    const { rewardId } = condoReviewBookStore;

    const currency = getSelectedCurrency(account);

    const postData: ICondoBookPackageWithCard = {
      ...bookWithCard,
      currency,
      guests: bookWithCard.guests.map((guest: ICondoGuestBooking) => {
        return {
          ...guest,
          givenName: guest.givenName.trim(),
          surname: guest.surname.trim(),
        };
      }),
    };

    if (selection === InsuranceSelection.YES && insurance?.quoteId) {
      postData.insuranceQuoteId = insurance?.quoteId;
    }
    if (rewardId != null) {
      postData.rewardId = rewardId;
    }

    try {
      const res = await axiosInstance.post(Urls.CondoBook, postData, {
        ...getHeaders(),
      });

      const bookingResponse = res?.data;
      //Stripe payment flow
      if (bookingResponse?.chargeStatus === StripeChargeStatus.PENDING_EXPRESS) {
        dispatch(
          confirmPayment(
            bookingResponse.clientSecret,
            stripe,
            elements,
            bookingResponse?.bookingGuid,
            paymentMethod,
            directConfirm,
          ),
        );
        dispatch(setThreeDSId(bookingResponse?.bookingGuid));
        dispatch(setLoadingCondoBooking(false));
      }

      if (bookingResponse?.chargeStatus === 'Pending3DS') {
        dispatch(setLoadingCondoBooking(false));
        dispatch(setThreeDSModalVisible(true));
        dispatch(setThreeDSId(bookingResponse?.bookingGuid));
        dispatch(setThreeDSUrl(bookingResponse?.urlRedirectTo3DS));
        dispatch(setThreeDSLoading(false));
        return;
      }

      //Normal flow (card)
      if (bookingResponse?.chargeStatus !== StripeChargeStatus.PENDING_EXPRESS)
        dispatch(handleProcessBookingComplete(bookingResponse));
      
    } catch (error) {
      dispatch(handleErrorBookingComplete(error, onErrorCallback));
    }
  };
};

export const getCondoBookPackageWithCard3DS = (sessionKey: ISessionKey): AppThunk => {
  return async (dispatch, getState) => {
    const { reviewBookStore, loginStore } = getState();
    const { threeDSId } = reviewBookStore;
    const { account } = loginStore;

    dispatch(setThreeDSLoading(true));

    const currency = getSelectedCurrency(account);

    try {
      const res = await axiosInstance.post(Urls.CondoBookResume, { bookingId: threeDSId, sessionKey, currency }, { ...getHeaders() });
      dispatch(handleProcessBookingComplete(res?.data));

      dispatch(setThreeDSModalVisible(false));
      dispatch(setThreeDSUrl(null));
      dispatch(setThreeDSId(null));
      dispatch(setThreeDSLoading(false));
    } catch (error) {
      dispatch(handleErrorBookingComplete(error, null));
    } finally {
      dispatch(setThreeDSModalVisible(false));
      dispatch(setThreeDSUrl(null));
      dispatch(setThreeDSId(null));
      dispatch(setThreeDSLoading(false));

      window.scrollTo(0, 0);
    }
  };
};

const handleProcessBookingComplete = (data: any): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(setLoadingCondoBooking(true));

    const { navigationMenuStore, loginStore } = getState();

    // TRAV23-3993: Stripe express resume responses report success in different fields across BFF
    // versions: legacy `status: "Succeeded"`, cars-style `bookingStatusEnum`/`chargeStatus:
    // "Confirmed"`, and some condo flows surface `status: "Confirmed"` directly. Match any of those.
    const CONDO_BOOKING_SUCCESS_VALUES: ReadonlyArray<string | undefined> = [
      STATUS_SUCCEEDED,
      STATUS_CONFIRMED,
    ];
    const isBookingConfirmed =
      CONDO_BOOKING_SUCCESS_VALUES.includes(data?.status) ||
      CONDO_BOOKING_SUCCESS_VALUES.includes(data?.bookingStatusEnum) ||
      CONDO_BOOKING_SUCCESS_VALUES.includes(data?.chargeStatus);

    if (isBookingConfirmed) {
      dispatch(clearUserCertificateSearchContext());
      dispatch(setCondoBookingComplete(true));
      localStorage.setItem(CONDO_BOOKING_GUEST_INFO_LABEL, '');
      localStorage.setItem(CONDO_BOOKING_CARD_INFO_LABEL, '');
      localStorage.setItem(CONDO_BOOKING_SAME_ADDRESS_INFO_LABEL, '');

      const { account, userWallet } = loginStore;

      if (navigationMenuStore?.items?.promo) {
        UrlUtils.removeFromUrl(CONDO_UNITS_SESSION_KEY_LABEL);
        UrlUtils.removeFromUrl(CONDO_SESSION_KEY);
        UrlUtils.removeFromUrl(CONDO_UNITS_SEARCH_LABEL);
      }

      dispatch(setSelectedCondoReviewClientCash(null));
      dispatch(setSelectedCondoReviewClientCashStr(''));
      dispatch(condosActions.setSelectedCondoSearchClientCash(null));
      dispatch(getUserWallet(userWallet));
      dispatch(getUserWalletReward(userWallet));
      dispatch(getAccountCreditLine());

      ReactGA.event({
        category: account.name,
        action: `${C_C_BOOKING_CONFIRMED}_${account.name.toUpperCase()}`,
        label: `Condo Booking confirmed on book`,
        nonInteraction: false,
      });

    } else if (data.status === STATUS_FAILED) {
      if (data.error?.errorCode) {
        const { errorCode } = data.error;

        dispatch(setBookingErrorCodeError(errorCode));
      } else {
        dispatch(setBookingErrorCodeError(BookingErrorsEnum.UnexpectedResponse));
      }

      dispatch(setIsAlternativeUnitsAvailable(!!data?.isAlternativeUnitsAvailable));
    } else if (data.status === STATUS_PENDING) {
      dispatch(setIsBookingPending(true));
    } else {
      dispatch(setCondoBookingInProgress(true));
    }

    dispatch(setCondoBooking(data));

    if (data?.insurance?.policyDetail?.policyStatus === 'Error') {
      dispatch(insuranceActions.setInsurance(null));
    }

    dispatch(setLoadingCondoBooking(false));

    window.scrollTo(0, 0);
  }
}

const handleErrorBookingComplete = (error: any, onErrorCallback: any): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoadingCondoBooking(false));
    dispatch(setThreeDSModalVisible(false));
    dispatch(setThreeDSUrl(null));
    dispatch(setThreeDSId(null));
    dispatch(setThreeDSLoading(false));

    if (error?.response?.data && error?.response?.data[zeroItem].errorType === VALIDATION_ERROR) {
      dispatch(setErrorsCondoField(error.response.data));
    } else if (
      error?.response?.data &&
      error?.response?.data[zeroItem].errorType === EXTERNAL_ERROR
    ) {
      dispatch(setCondoExternalError(true));
    } else if (
      error?.response?.data &&
      error?.response?.data[zeroItem].errorCode === PRICE_CHANGED
    ) {
      dispatch(setCondoPriceChangedError(true));
    } else if (error?.response?.data && error?.response?.data[zeroItem].errorType === ERROR) {
      dispatch(setCondoErrorsBookingMessage(error.response.data));
    } else if (error?.response?.data[zeroItem].errorCode === SAVING_MISMATCH) {
      dispatch(setCondoExpiredSession(true));
      dispatch(setCondoSavingsMismatch(true));
    } else {
      const errorObject = isString(error?.response?.data) ? JSON.parse(error?.response?.data) : error?.response?.data;
      const errorMessage = get(errorObject, 'code', null);
      const errorReason = get(errorObject, 'reason', error.toString());

      const paymentErrors = [CHECK_OUT_PAYMENT_ERROR, DECLINE_PAYMENT_ERROR, STATE_PAYMENT_ERROR, POSTAL_CODE_PAYMENT_ERROR];
      const headerError = [BookingErrorsEnum.General, BookingErrorsEnum.RoomsUnavailable, BookingErrorsEnum.SoldOut];
      const errors = [BookingErrorsEnum.InvalidPayload, BookingErrorsEnum.CheckoutError]; 
      if (paymentErrors.includes(errorMessage)) {
        dispatch(setBookingErrorCodeError(errorReason));
        onErrorCallback ? onErrorCallback(errorMessage) : null;
      } else if (headerError.includes(errorMessage)) {
        dispatch(setBookingErrorCodeError(errorMessage));
        onErrorCallback ? onErrorCallback(errorMessage) : null;
      } else if (errors.includes(errorMessage)) {
          dispatch(setBookingErrorCodeError(errorReason));
      } else {
        dispatch(setCondoErrorBooking(errorMessage ? `error.code.${errorMessage}` : errorReason));
      }
    }
  }
}